// One request and everything under it: the delegation tree, each task's result
// and its timeline. Polls while anything is still working.
import { useState } from "react";
import { Loader2, ChevronDown, ChevronRight, XCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Markdown } from "@/components/common/Markdown";
import { useApi } from "@/hooks/useApi";
import { api } from "@/lib/api";
import { fmtMs, fmtWhen, isActive, providerLabel, TaskStatusBadge, type TaskDetail, type TaskEvent, type TaskNode } from "./shared";

const KIND_LABEL: Record<string, string> = {
  tool_call: "Used a tool", tool_result: "Tool returned", delegated: "Handed off", delegation_results: "Team reported back",
  ask_human: "Asked you", approval_requested: "Asked for approval", message: "Wrote", error: "Problem", note: "Note", thought: "Thought",
};

export function TaskView({ taskId, canRun, onChanged }: { taskId: number; canRun: boolean; onChanged: () => void }) {
  const { data, reload } = useApi<TaskDetail>(`/api/agents/tasks/${taskId}`, { refetchInterval: 2500 });
  if (!data) return <p className="py-6 text-center text-sm text-muted-foreground"><Loader2 className="mr-2 inline size-4 animate-spin" />Loading…</p>;
  const root = data.tasks.find((t) => t.id === data.rootId) ?? data.tasks[0];
  if (!root) return null;
  const active = data.tasks.some((t) => isActive(t.status));
  const after = () => { void reload(); onChanged(); };

  return (
    <div className="space-y-3">
      <Card className="space-y-2 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-semibold">{root.title}</p>
            <p className="text-xs text-muted-foreground">
              {root.agent_name}{root.shop_name ? ` · ${root.shop_name}` : ""} · asked {fmtWhen(root.created_at)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {active && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
            <TaskStatusBadge status={root.status} />
            {canRun && isActive(root.status) && <CancelButton id={root.id} onDone={after} />}
          </div>
        </div>
        {root.instructions !== root.title && <p className="whitespace-pre-wrap text-sm text-muted-foreground">{root.instructions}</p>}
        {root.status === "done" && root.result && <Result text={root.result} />}
        {root.error && <p className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{root.error}</p>}
      </Card>

      {data.tasks.length > 1 && <p className="text-xs font-medium text-muted-foreground">The team on this</p>}
      <div className="space-y-2">
        {data.tasks.filter((t) => t.id !== root.id).map((t) => (
          <NodeCard key={t.id} node={t} events={data.events.filter((e) => e.task_id === t.id)} canRun={canRun} onChanged={after} />
        ))}
      </div>
      <NodeTimeline events={data.events.filter((e) => e.task_id === root.id)} label="What the lead did" />
      {data.tasks.some((t) => t.status === "waiting_approval") && (
        <Card className="border-cta/50 p-3 text-sm">Something here is waiting for approval before it goes to a client. Open the <strong>Approvals</strong> tab to review it.</Card>
      )}
      {root.status === "waiting_human" && canRun && <AnswerBox task={root} events={data.events} onDone={after} />}
    </div>
  );
}

function Result({ text }: { text: string }) {
  return <div className="rounded-md border border-border bg-background p-3"><Markdown text={text} /></div>;
}

function CancelButton({ id, onDone }: { id: number; onDone: () => void }) {
  return (
    <Button size="sm" variant="ghost" className="text-destructive" onClick={async () => {
      try { await api.post(`/api/agents/tasks/${id}/cancel`, {}); toast.success("Cancelled"); onDone(); }
      catch (e) { toast.error(e instanceof Error ? e.message : "Couldn't cancel"); }
    }}><XCircle className="mr-1 size-3.5" />Cancel</Button>
  );
}

function NodeCard({ node, events, canRun, onChanged }: { node: TaskNode; events: TaskEvent[]; canRun: boolean; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginLeft: Math.min(node.depth - 1, 3) * 20 }} className="border-l-2 border-border pl-3">
      <Card className="space-y-1.5 p-3">
        <button type="button" className="flex w-full items-start justify-between gap-2 text-left" onClick={() => setOpen((o) => !o)}>
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium">{node.agent_name ?? "Teammate"} <span className="font-normal text-muted-foreground">· {node.title}</span></span>
            <span className="block text-[11px] text-muted-foreground">{node.step_count} step{node.step_count === 1 ? "" : "s"}</span>
          </span>
          <span className="flex shrink-0 items-center gap-1.5"><TaskStatusBadge status={node.status} />{open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}</span>
        </button>
        {open && (
          <div className="space-y-2">
            {node.instructions && <p className="whitespace-pre-wrap text-xs text-muted-foreground">{node.instructions}</p>}
            {node.result && <Result text={node.result} />}
            {node.error && <p className="text-xs text-destructive">{node.error}</p>}
            {canRun && isActive(node.status) && <CancelButton id={node.id} onDone={onChanged} />}
            <NodeTimeline events={events} label="Timeline" />
          </div>
        )}
      </Card>
    </div>
  );
}

function NodeTimeline({ events, label }: { events: TaskEvent[]; label: string }) {
  const [open, setOpen] = useState(false);
  if (events.length === 0) return null;
  return (
    <div className="text-xs">
      <button type="button" className="flex items-center gap-1 text-muted-foreground hover:text-foreground" onClick={() => setOpen((o) => !o)}>
        {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}{label} ({events.length})
      </button>
      {open && (
        <ol className="mt-1.5 space-y-1.5 border-l border-border pl-3">
          {events.map((e) => (
            <li key={e.id}>
              <span className={`font-medium ${e.kind === "error" ? "text-destructive" : ""}`}>{KIND_LABEL[e.kind] ?? e.kind}</span>
              {e.provider && <span className="text-muted-foreground"> · {providerLabel(e.provider)} {e.model} · {fmtMs(e.duration_ms)}</span>}
              {e.content && <p className="mt-0.5 line-clamp-6 whitespace-pre-wrap break-words text-muted-foreground">{e.content}</p>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function AnswerBox({ task, events, onDone }: { task: TaskNode; events: TaskEvent[]; onDone: () => void }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const question = [...events].reverse().find((e) => e.task_id === task.id && e.kind === "ask_human")?.content;
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/api/agents/tasks/${task.id}/message`, { text }); setText(""); onDone(); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Couldn't send"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="space-y-2 border-cta/50 p-3">
      <p className="text-sm font-medium">{task.agent_name} needs your answer</p>
      {question && <p className="whitespace-pre-wrap text-sm text-muted-foreground">{question}</p>}
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} className="text-sm" placeholder="Your answer…" />
      <div className="flex justify-end"><Button size="sm" onClick={send} disabled={busy || !text.trim()}><Send className="mr-1 size-3.5" />Send</Button></div>
    </Card>
  );
}
